import { Fragment, useMemo } from "react";
import type { ByPercentagem, ItemData } from "~/application/types/entities/CustomerBI.type";
import { Card } from "~/components/Card";
import { Flex } from "~/components/Flex";
import { Icon } from "~/components/Icon";
import { SvgArrowDown, SvgArrowUp } from "~/components/Icon/icons";
import { Text } from "~/components/Text";
import { getServiceIcon, getServiceLabel } from "~/core/shared/utils/order.utils";
import { asCurrency } from "~/utils/mask.utils";

export interface ChartByItemProps {
  chartByItem?: ItemData[];
  percentageByItem?: ByPercentagem[];
}

export function ChartByItem({ chartByItem, percentageByItem }: ChartByItemProps) {
  const formatPercentage = (value: number) => {
    return Number(value.toFixed(2)) % 1 === 0 ? `${value.toFixed(0)}%` : `${value.toFixed(2)}%`;
  };

  const dataChartByItem = useMemo(() => {
    return chartByItem?.map((item) => {
      const percentBrokenPolicy = item.percentBrokenPolicy;

      return {
        name: item.item,
        value: item.value,
        percentValue: percentBrokenPolicy,
        percentages: [
          { name: "Fora da política", value: percentBrokenPolicy, item: item.item },
          {
            name: "Dentro da política",
            value: percentBrokenPolicy > 0 ? 100 - percentBrokenPolicy : 100,
            item: item.item,
          },
        ],
      };
    });
  }, [chartByItem]);

  return (
    <Fragment>
      {(!dataChartByItem || dataChartByItem.length === 0) && (
        <Card
          css={{
            overflow: "hidden",
            height: "100%",
            width: "100%",
          }}
        >
          <Flex justify="center" align="center" css={{ height: "100%", width: "100%" }}>
            <Text fw="700">Nenhum dado disponível</Text>
          </Flex>
        </Card>
      )}

      {dataChartByItem?.map((item, index) => (
        <Card
          key={`${item.name}-${index}`}
          css={{
            height: "100%",
            overflow: "hidden",
            width: "auto",
          }}
        >
          <Flex css={{ width: "100%", height: "100%" }}>
            <Flex
              justify="center"
              align="center"
              css={{ background: "$primary-base", p: "$5", height: "auto" }}
            >
              <Icon fill="#fff" as={getServiceIcon(item.name)} />
            </Flex>
            <Flex
              gap="4"
              justify="between"
              css={{ px: "$5", py: "$4", width: "100%", flex: "1" }}
              direction="column"
            >
              <Flex direction="column" gap="5">
                <Text css={{ fontWeight: "bold" }}>{getServiceLabel(item.name)}</Text>
                <Text size="6" fw="700">
                  {asCurrency(item.value)}
                </Text>
              </Flex>
              <Flex
                gap="4"
                align="start"
                direction="column"
                justify="center"
                css={{ width: "100%", height: "50px" }}
              >
                <Flex gap="3" justify="center" align="center">
                  <Icon as={SvgArrowUp} />
                  <Text>
                    {formatPercentage(item.percentages?.at(1)?.value ?? 0)} dentro da política
                  </Text>
                </Flex>
                <Flex gap="3">
                  <Icon as={SvgArrowDown} />
                  <Text>
                    {formatPercentage(item.percentages?.at(0)?.value ?? 0)} fora da política
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      ))}
    </Fragment>
  );
}
